import { ValidatorFn, Validators } from '@angular/forms';
import type { Property } from '../../core/models/property.model';

/**
 * Create a standard collection of Angular Form validators based on property data.
 * @param property The property
 */
export const createStandardControlValidatorsForProperty = (property: Property): ValidatorFn[] => {
  const validators: ValidatorFn[] = [];

  if (property.Rules.RequiredIf) {
    validators.push(Validators.required);
  }

  return validators;
};

/**
 * Create a collection of Angular Form validators for a numeric property. Includes all base validators too.
 * @param property The property
 */
export const createNumericControlValidatorsForProperty = (property: Property): ValidatorFn[] => {
  const validators = createStandardControlValidatorsForProperty(property);

  const { SetMinValue, SetMaxValue } = property.Rules;

  if (SetMinValue !== null && SetMinValue !== undefined) {
    validators.push(Validators.min(SetMinValue));
  }
  if (SetMaxValue !== null && SetMaxValue !== undefined) {
    validators.push(Validators.max(SetMaxValue));
  }

  return validators;
};

/**
 * Parses a string into a number, ignoring all non-numeric characters and handling the thousand- and decimal-separators.
 * @param value The string value to parse
 * @param formatter The number format
 * @return The numeric value, NaN if value cannot be parsed as number
 */
export const parseNumberFromString = (value: string, formatter: Intl.NumberFormat): number => {
  if (!value) {
    return Number.NaN;
  }

  // Get current separators for parsing
  const separators = getSeparators(formatter);

  // Split based on decimal point (note that there may not be a decimal)
  const separatedByDecimal = value.split(separators.decimal);

  let numericValue: number;

  // Removes non-numeric characters
  const replaceNonNumeric = (v: string): string => v.replace(/[^\-0-9]/g, '');

  // If separated value count is one, then there's no decimal -> parse directly
  if (separatedByDecimal.length === 1) {
    numericValue = Number.parseFloat(replaceNonNumeric(value));
  } else {
    // Get parts on either side of the decimal and remove non-numeric digits from both. Then combine with the standard
    // decimal separator '.'
    const leftOfDecimalParts = separatedByDecimal.slice(0, separatedByDecimal.length - 1);
    const leftOfDecimal = leftOfDecimalParts.map(replaceNonNumeric).join('');

    const rightOfDecimal = replaceNonNumeric(separatedByDecimal[separatedByDecimal.length - 1]);

    const numericString = `${leftOfDecimal}.${rightOfDecimal}`;

    // Parse the final string as a float
    numericValue = Number.parseFloat(numericString);
  }

  // If invalid, return NaN
  if (Number.isNaN(numericValue)) {
    return Number.NaN;
  }

  // Get number of decimals, round if necessary
  const { minimumFractionDigits, maximumFractionDigits } = formatter.resolvedOptions();

  if (minimumFractionDigits + maximumFractionDigits > 0) {
    const decimalFactor = Number.parseFloat(`1e-${minimumFractionDigits}`);
    return Math.round(numericValue / decimalFactor) * decimalFactor;
  }

  return Math.round(numericValue);
};

export interface Separators {
  thousand: string | null;
  decimal: string;
}

/**
 * Get the separators for the current given locale. Uses browser locale if no locale provided.
 * @param numberFormat Optional, the locale to use
 */
export const getSeparators = (numberFormat: Intl.NumberFormat | null): Separators => {
  const exampleValue = 1000.1;
  const formattedValue = numberFormat.formatToParts(exampleValue);

  const thousand = formattedValue.find(v => v.type === 'group');
  const decimal = formattedValue.find(v => v.type === 'decimal');

  return {
    decimal: decimal?.value,
    thousand: thousand?.value
  };
};
